import React, { useState } from 'react';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/solid';
import DOMPurify from 'dompurify';
import { usePortfolioData } from '../context/PortfolioDataContext';
import SectionWithNeuralBg from './SectionWithNeuralBg';

export default function Testimonials() {
  const { data } = usePortfolioData();
  const testimonials = data.testimonials;
  const [showAll, setShowAll] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleReadMore = (index) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedIds(newExpanded);
  };

  const truncateText = (text, maxLength = 200) => {
    const strippedText = text.replace(/<br\s*\/?>/gi, ' ');
    if (strippedText.length <= maxLength) return text;
    return strippedText.substring(0, maxLength) + '...';
  };

  const displayedTestimonials = showAll ? testimonials : testimonials.slice(0, 3);

  return (
    <SectionWithNeuralBg id="testimonials" className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary-900/30 rounded-full flex items-center justify-center border border-primary-800/50">
              <ChatBubbleBottomCenterTextIcon className="w-8 h-8 text-primary-400" />
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Testimonials
          </h2>
          <p className="text-lg text-gray-400">
            What colleagues and clients say about working with me
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {displayedTestimonials.map((testimonial, index) => {
            const isExpanded = expandedIds.has(index);
            const isLong = testimonial.quote.replace(/<br\s*\/?>/gi, ' ').length > 200;
            const displayQuote = isExpanded ? testimonial.quote : truncateText(testimonial.quote);

            return (
              <div
                key={index}
                className="card group hover:border-primary-700 hover:shadow-xl hover:shadow-primary-900/20 transition-all duration-300 flex flex-col">
                {/* Author Info */}
                <div className="mb-4">
                  <h4 className="text-white font-semibold text-xl mb-1 group-hover:text-gradient transition-all">
                    {testimonial.name}
                  </h4>
                  <p className="text-primary-400 text-sm font-medium">
                    {testimonial.company}
                  </p>
                </div>

                {/* Testimonial Text */}
                <div className="flex-1">
                  <p 
                    className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayQuote) }}
                  />
                  
                  {/* Read More Button */}
                  {isLong && (
                    <button
                      onClick={() => toggleReadMore(index)}
                      className="text-primary-400 hover:text-primary-300 text-sm font-medium mt-4 inline-flex items-center transition-colors">
                      {isExpanded ? 'Read Less' : 'Read More'}
                      <svg 
                        className={`ml-1 w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        {!showAll && testimonials.length > 3 && (
          <div className="text-center mt-12">
            <button
              onClick={() => setShowAll(true)}
              className="btn-primary">
              View All Testimonials
            </button>
          </div>
        )}
      </div>
    </SectionWithNeuralBg>
  );
}
